import { useEffect, useRef, useState } from 'react'
import { ChevronsUpDown, Search } from 'lucide-react'
import type { Suggestion } from './SuggestionBar'

interface AllSuggestionsPopoverProps {
  all: Suggestion[]
  onPick: (s: Suggestion) => void
}

/**
 * Expand chip + popover listing every active canned answer for the account.
 * Used when the ranker's top picks aren't right and the agent wants to scan
 * the full library without leaving the conversation.
 */
export function AllSuggestionsPopover({ all, onPick }: AllSuggestionsPopoverProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  // Click-outside to close
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const q = query.toLowerCase().trim()
  const filtered = q
    ? all.filter(s =>
        s.title.toLowerCase().includes(q) ||
        s.body.toLowerCase().includes(q) ||
        s.tags.some(t => t.toLowerCase().includes(q))
      )
    : all

  if (all.length === 0) return null

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title={`Browse all ${all.length} canned answers`}
        className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted hover:bg-muted/70 text-muted-foreground text-xs cursor-pointer border border-border"
      >
        <ChevronsUpDown className="size-3" />
        <span>All ({all.length})</span>
      </button>

      {open && (
        <div className="absolute bottom-full right-0 mb-2 w-80 bg-card border border-border rounded-lg shadow-xl z-30 overflow-hidden">
          <div className="px-3 py-2 border-b border-border flex items-center gap-2">
            <Search className="size-3.5 text-muted-foreground shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Filter canned answers…"
              className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">No matches</div>
            ) : (
              filtered.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); onPick(s); setOpen(false); setQuery('') }}
                  className="w-full text-left px-3 py-2 hover:bg-muted cursor-pointer flex flex-col gap-0.5"
                >
                  <span className="text-sm font-medium">{s.title}</span>
                  <span className="text-xs text-muted-foreground truncate font-mono">{s.body}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
